import { use, useEffect } from 'react';
import useUser from '@hooks/user/use-user';
import { useTranslation } from 'next-i18next';
import { TimelineContext } from '@context/timeline.context';
import { useMutation } from '@tanstack/react-query';
import { regenerateShippingLabel, RegenerateShippingLabelType } from '@services/my-orders-sales-service';
import { RegenerateShippingLabel } from '@interfaces/api/responses/my-sale';
import { StepInfoContext } from '@context/step-info.context';
import { EmptyResponse } from '@interfaces/api/responses/timeline-action';
import { AxiosError } from 'axios';
import useTimelineQuery from '@hooks/orders-sales/use-timeline-query';
import { ShipmentLabelTypeEnum } from '@interfaces/models/timeline';
import useGetShippingAddresses from '@hooks/orders-sales/use-get-shipping-address';
import popinStyles from '../../../popin.module.scss';
import { SubmitErrorModal } from '../error-modal';
import AddressModalWrapper from '../../address-form-web-component/address-wrapper';
import AddressModal, { AdserType } from '../../address-form-web-component/address-modal';

const LANG_PREFIX = 'MY_ACCOUNT.ORDERS_SALES.POPUP.GET_SHIPPING_LABEL.';
const ADDRESS_CONTAINER_CLASSNAME = 'get-shipping-label-address-form-container';

const ShippingAddressModal = () => {
  const { t } = useTranslation(['orders-sales', 'default-layout']);

  const { user } = useUser();

  const { selectedProduct: product, showToast } = use(TimelineContext);

  const { showAddressForm, isShowAddressForm, shipmentDetails } = use(StepInfoContext);

  const { refetchTimeline } = useTimelineQuery(product.productId, null, false);

  const { selectedAddress: address, isLoading: addressLoading, refetch } = useGetShippingAddresses(isShowAddressForm);

  const {
    mutate,
    isLoading: isMutating,
    error: submitError,
    reset,
  } = useMutation<RegenerateShippingLabel, AxiosError<EmptyResponse>, RegenerateShippingLabelType>({
    mutationFn: (param: RegenerateShippingLabelType) => regenerateShippingLabel(param),
  });

  const onCloseAddressModal = () => {
    showAddressForm(false);
  };

  const onCreatedAddressEvent = () => {
    refetch();

    // In case no label generated yet, just close the address modal
    if (!shipmentDetails) {
      onCloseAddressModal();
      return;
    }

    mutate(
      { productId: product.productId },
      {
        onSuccess: (res) => {
          const { isReAllocationNeeded, productsWaitingToShip, labelType, shippingLabelUrl } = res || {};

          let toastMsg = isReAllocationNeeded
            ? t('MY_ACCOUNT.ORDERS_SALES.TOAST.ADDRESS_UPDATED_SETUP_SHIPPING_DETAILS')
            : t('MY_ACCOUNT.ORDERS_SALES.TOAST.ADDRESS_UPDATED_USED_SHIPPING_LABEL');

          const hasOtherWaitingShipItems =
            productsWaitingToShip?.length && !!productsWaitingToShip.filter((p) => p.id !== product.productId).length;

          if (hasOtherWaitingShipItems) {
            toastMsg = toastMsg.concat(
              `<br/>${t('MY_ACCOUNT.ORDERS_SALES.TOAST.ADDRESS_UPDATED_CHECK_OTHER_SHIP_DETAIL')}`,
            );
          }

          if (labelType === ShipmentLabelTypeEnum.printableLabel && shippingLabelUrl) {
            window.open(shippingLabelUrl, '_blank');
          }

          showToast({
            title: toastMsg,
            type: 'success',
          });

          refetchTimeline();
          onCloseAddressModal();
        },
        onError: () => {
          onCloseAddressModal();
        },
      },
    );
  };

  // We do not want user to edit the country, phone number prefix
  // One solution is to update AddressForm (introduce another context)
  // But for the sake of simplicity we disable the field manually here
  useEffect(() => {
    const targetNode = document.querySelector(`.${ADDRESS_CONTAINER_CLASSNAME}`);
    if (!targetNode) {
      return;
    }
    const observer = new MutationObserver(() => {
      for (const hideClass of ['country', 'countryList']) {
        const element = document.querySelector(`select[name="${hideClass}"]`);
        if (element) {
          element.setAttribute('disabled', 'true');
        }
      }
    });

    observer.observe(targetNode, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [isShowAddressForm]);

  if (addressLoading) {
    return (
      <AddressModalWrapper
        title={t(`${LANG_PREFIX}SHIPPING`)}
        open={isShowAddressForm}
        onClose={onCloseAddressModal}
      >
        <div className={popinStyles.ordersSales__popin__loading} />
      </AddressModalWrapper>
    );
  }
  if (!address) {
    return (
      <AddressModalWrapper
        title={t(`${LANG_PREFIX}SHIPPING`)}
        open={isShowAddressForm}
        onClose={onCloseAddressModal}
      >
        <p>{t('MY_ACCOUNT.ORDERS_SALES.ERROR.API')}</p>
      </AddressModalWrapper>
    );
  }
  if (isMutating) {
    return (
      <AddressModalWrapper
        open={true}
        title={t('MY_ACCOUNT.ORDERS_SALES.POPUP.REGENERATE_SHIPPING_LABEL')}
      >
        <div className={popinStyles.ordersSales__popin__loading} />
      </AddressModalWrapper>
    );
  }

  const adserProps: AdserType = {
    displayScreen: 'inPage',
    nextStepDisplay: 'inPage',
    screen: 'default',
    context: 'account',
    editAddress: {
      clientId: user.id,
      country: address.country.iso2,
      id: address.addressId.toString(),
      onSuccessCallback: onCreatedAddressEvent,
    },
  };

  return (
    <>
      <AddressModal
        title={t(`${LANG_PREFIX}SHIPPING`)}
        adserProps={adserProps}
        isOpen={isShowAddressForm}
        onClose={onCloseAddressModal}
      />

      {!!submitError && (
        <SubmitErrorModal
          productId={product.productId}
          onClose={reset}
          errorMessage={submitError.response?.data?.errors?.detail?.toString()}
          errorObjects={submitError.response?.data?.errors?.objects}
          onGenerateReviewAddress={() => {
            showAddressForm(true);
            reset();
          }}
          onTicketSupportSent={() => {
            showToast({
              title: t('MY_ACCOUNT.ORDERS_SALES.POPUP.GET_SHIPPING_LABEL.GENERATION_FAILED_DESCRIPTION_ZENDESK'),
              type: 'success',
            });
            reset();
          }}
        />
      )}
    </>
  );
};

export default ShippingAddressModal;
